import { makeRandomString } from '../utils/general';
import * as events from 'events';
const emitter = new events.EventEmitter();

const allyID = 'includeai-ally-launcherFrame';
const allySidebarID = 'includeai-ally-sidebarFrame';

function respondWithSecret(secret: string) {
	return (data: any) => {
		emitter.emit('response', {
			secret,
			data,
		});
	};
}

export class Channel {
	public static getInstance(context: 'background' | 'content' | 'ally' | 'allysidebar') {
		if (!Channel.instance) {
			switch (context) {
				case 'background':
					Channel.instance = new BackgroundChannel();
					break;

				case 'content':
					Channel.instance = new ContentChannel();
					break;

				case 'ally':
					Channel.instance = new AllyChannel();
					break;

				case 'allysidebar':
					Channel.instance = new AllySidebarChannel();
					break;

				default:
					break;
			}
		}
		return Channel.instance;
	}
	
	private static instance: Channel;
	public stateChangingEvents: string[];
	public context: 'background' | 'content' | 'ally' | 'allysidebar' | 'hq' | null;
	public handlers: any;
	public stagedMessages: any;
	public specialIndicator: string;

	constructor() {
		this.context = null;
		this.handlers = {};
		this.stateChangingEvents = [];
		this.stagedMessages = {};
		this.specialIndicator = '';
	}
	
	public ingestHandlers(handlers: any) {
		this.handlers = {
			...this.handlers,
			...handlers,
		};
	}

	public ingestStateChangingEvents(events: string[]) {
		this.stateChangingEvents = events;
	}

	public removeHandlerWithName(handlerName: string) {
		delete this.handlers[handlerName];
	}

	public stageMessageForKey(key: any, msg: any) {
		this.stagedMessages[key] = msg;
	}

	public flushMessageForKey(key: any) {
		if (!!this.stagedMessages[key]) {
			this.emit(this.stagedMessages[key]);
			delete this.stagedMessages[key];
		}
	}

	public emit(event: any) {
		const command = event.command;
		const args = event.args;
		const to = event.to;
		const options = !!event.options ? event.options : {};
		const secret = 'secret_' + makeRandomString(16);
		return new Promise((resolve) => {
			emitter.addListener('response', function listener(msg) {
				if (msg.secret === secret) {
					emitter.removeListener('response', listener);
					resolve(msg.data);
				}
			});
			emitter.emit('sameContextInbound', {
				command,
				args,
				secret,
				options,
			});
		});
	}

	public waitForInboundMessage(event: string, maxTimeout: number, secret: string | null = null) {
		// return promise for receiving another 
		return new Promise((resolve) => {
			const listener = (msg: any) => {
				if (!!secret) {
					// check secret in msg
					if (secret === msg.emitterSecret) {
						emitter.removeListener(event, listener);
						resolve(true);
					}
				} else {
					emitter.removeListener(event, listener);
					resolve(true);
				}
			};
 
			emitter.addListener(event, listener);
			window.setTimeout(() => {
				emitter.removeListener(event, listener);
				resolve(false); 
			}, maxTimeout);
		});
	}

	public setSpecialIndicator(indicator: string) {
		this.specialIndicator = indicator;
	}
}

/*
Channel For background page
*/
const backgroundAllowedEvents = [
	'networkEvent',
	'navigationEvent'
];
class BackgroundChannel extends Channel {
	constructor() {
		super();
		this.context = 'background';

		emitter.addListener('sameContextInbound', this.listenToSameContext.bind(this));
		// @ts-ignore
		chrome.runtime.onMessage.addListener(this.listenToPage.bind(this));
		// @ts-ignore
		chrome.runtime.onMessageExternal.addListener(this.listenToPage.bind(this));
		this.bindChromeComandListener();
	}

	public listenToSameContext(msg: any) {
		if (!!msg.command) {
			const secret = msg.secret;
			const response = respondWithSecret(secret);
			if (this.stateChangingEvents.includes(msg.command)) { // state changing event
				emitter.emit(msg.command, { emitterSecret: msg.options.secret });
				// @ts-ignore
				window.sm.routeEventToMachine(msg.command, msg, msg.options, response);
			} else if (Object.keys(this.handlers).includes(msg.command)) {
				emitter.emit(msg.command, { emitterSecret: msg.options.secret });
				this.handlers[msg.command](response, msg.options, msg.args);
			} else if (backgroundAllowedEvents.includes(msg.command)) {
				emitter.emit(msg.command);
			} else {
				response(`No command found for ${msg.command}`);
			}
		}
		return true;
	}

	public listenToPage(msg: any, sender: any, response: any) {
		const tabData = { tab: sender.tab ? sender.tab.id : null }; // accomodate for popup
		const metadata = {
			...msg.options, ...tabData,
		};
		if (msg.command === 'routeToContent') {
			const payload = msg.payload;
			const tab = { tab: sender.tab.id };
			this.emit({
				command: payload.command,
				args: payload.args,
				to: 'content',
				options: {
					...payload.options,
					...tab,
				},
			}).then((result) => {
				response(result);
			});
		} else if (this.stateChangingEvents.includes(msg.command)) { // state changing events, handled separately
			emitter.emit(msg.command, { emitterSecret: msg.options.secret });
			// @ts-ignore
			window.sm.routeEventToMachine(msg.command, msg, metadata, response);
		} else if (Object.keys(this.handlers).includes(msg.command)) {
			emitter.emit(msg.command, { emitterSecret: msg.options.secret });
			this.handlers[msg.command](response, metadata, msg.args);
		} else if (backgroundAllowedEvents.includes(msg.command)) {
			emitter.emit(msg.command); // only for monitoring network and navigation events
		} else {
			response(`No command found for ${msg.command}`);
		}
		return true;
	}

	public bindChromeComandListener() {
		// @ts-ignore
		chrome.commands.onCommand.addListener((command: string) => {
			// @ts-ignore
			chrome.tabs.query({active: true, currentWindow: true}, (tabs) => {
				this.emit({
					command,
					to: 'background',
					options: {},
				});
			});
		});
	}

	public emit(event: any) {
		const command = event.command;
		const args = !!event.args ? event.args : [];
		const to = event.to;
		const options = !!event.options ? event.options : {};
		if (to === 'content') {
			const tab = options.tab;
			return new Promise((resolve) => {
				// @ts-ignore
				chrome.tabs.sendMessage(tab, {
					command,
					args,
					options,
				}, (response: any) => {
					resolve(response);
				});
			});
		} else if (to === 'hq') {
			options.specialInd = 'CONTENT';
			return new Promise((resolve) => {
				// @ts-ignore
				chrome.tabs.sendMessage(options.tab, {
					command: 'routeToHQ',
					payload: {
						command,
						args,
						options,
					},
				}, (response: any) => {
					resolve(response);
				});
			});
		} else if (to === 'ally') {
			options.specialInd = 'CONTENT';
			return new Promise((resolve) => {
				// @ts-ignore
				chrome.tabs.sendMessage(options.tab, {
					command: 'routeToAlly',
					payload: {
						command,
						args,
						options,
					},
				}, (response: any) => {
					resolve(response);
				});
			});
		} else if (to === 'allysidebar') {
			options.specialInd = 'CONTENT';
			return new Promise((resolve) => {
				// @ts-ignore
				chrome.tabs.sendMessage(options.tab, {
					command: 'routeToAllySidebar',
					payload: {
						command,
						args,
						options,
					},
				}, (response: any) => {
					resolve(response);
				});
			});
		} else {
			return super.emit(event);
		}
	}
}



/*
Channel For content page
*/
function respondToIFramesWithSecret(secret: string, id: string) {
	const iframe = document.getElementById(id);
	return (payload: any) => {
		// @ts-ignore
		iframe.contentWindow.postMessage({
			source: 'INCLUDEAI_CONTENT_PAGE',
			secret,
			payload,
			response: true
		}, '*');
	};
}

class ContentChannel extends Channel {
	constructor() {
		super();
		this.context = 'content';

		// @ts-ignore
		chrome.runtime.onMessage.addListener(this.listenToBackground.bind(this));
		window.addEventListener('message', this.listenToAlly.bind(this));
		window.addEventListener('message', this.listenToAllySidebar.bind(this));
	}

	public listenToSameContext(msg: any) {
		if (!!msg.command) {
			const secret = msg.secret;
			const response = respondWithSecret(secret);
			if (Object.keys(this.handlers).includes(msg.command)) {
				emitter.emit(msg.command, { emitterSecret: msg.options.secret });
				this.handlers[msg.command](response, msg.options, msg.args);
			} else {
				response(`No command found for ${msg.command}`);
			}
		}
		return true;
	}

	public listenToBackground(msg: any, sender: any, response: any) {
		if (msg.command === 'routeToAlly' && msg.payload.options.specialInd === this.specialIndicator) {
			const payload = msg.payload;
			this.emit({
				command: payload.command,
				args: payload.args,
				to: 'ally',
				options: payload.options,
			}).then((result) => {
				response(result);
			});
		} else if (msg.command === 'routeToAllySidebar' && msg.payload.options.specialInd === this.specialIndicator) {
			const payload = msg.payload;
			this.emit({
				command: payload.command,
				args: payload.args,
				to: 'allysidebar',
				options: payload.options,
			}).then((result) => {
				response(result);
			});
		} else if (msg.command === 'routeToHQ' && msg.payload.options.specialInd === this.specialIndicator) {
			const payload = msg.payload;
			this.emit({
				command: payload.command,
				args: payload.args,
				to: 'hq',
				options: payload.options,
			}).then((result) => {
				response(result);
			});
		} else if (Object.keys(this.handlers).includes(msg.command)) {
			emitter.emit(msg.command, { emitterSecret: msg.options.secret });
			this.handlers[msg.command](response, msg.options, msg.args);
		}
		return true;

	}

	public listenToAlly(packet: any) {
		if (packet.data.source === 'INCLUDEAI_IFRAME_CHANNEL' &&
			!packet.data.response) {
			const secret = packet.data.secret;
			const command = packet.data.command;
			const args = packet.data.args;
			const options = packet.data.options;
			const response = respondToIFramesWithSecret(secret, allyID);
			if (Object.keys(this.handlers).includes(command)) {
				emitter.emit(command, { emitterSecret: options.secret });
				this.handlers[command](response, options, args);
			} else {
				response(`No command found for ${command}`);
			}
		}
		return true;
	}

	public listenToAllySidebar(packet: any) {
		if (packet.data.source === 'INCLUDEAI_SIDEBAR_CHANNEL' &&
			!packet.data.response) {
			const secret = packet.data.secret;
			const command = packet.data.command;
			const args = packet.data.args;
			const options = packet.data.options;
			const response = respondToIFramesWithSecret(secret, allySidebarID);
			if (Object.keys(this.handlers).includes(command)) {
				emitter.emit(command, { emitterSecret: options.secret });
				this.handlers[command](response, options, args);
			} else {
				response(`No command found for ${command}`);
			}
		}
		return true;
	}

	public emit(event: any) {
		const command = event.command;
		const args = !!event.args ? event.args : [];
		const to = event.to;
		const options = !!event.options ? event.options : {};
		if (to === 'background') {
			return new Promise((resolve) => {
				// @ts-ignore
				chrome.runtime.sendMessage({
					command,
					args,
					options,
				}, (response: any) => {
					resolve(response);
				});
			});
		} else if (to === 'hq') {
			const secret = 'secret_content_to_hq_' + makeRandomString(16);
			return new Promise((resolve) => {
				window.addEventListener('message', function hqListener(event) {
					if (event.data.secret === secret &&
						event.data.source === 'INCLUDEAI_HQ_CHANNEL' &&
						event.data.response) {
						window.removeEventListener('message', hqListener);
						resolve(event.data.payload);
					}
				});

				parent.postMessage({
					secret,
					command,
					args,
					options,
					source: 'INCLUDEAI_CONTENT_PAGE',
				}, '*');
			});
		} else if (to === 'ally') {
			const iframe = document.getElementById(allyID);
			const secret = 'secret_content_to_ally_' + makeRandomString(16);
			return new Promise((resolve) => {
				window.addEventListener('message', function allyListener(event) {
					if (event.data.secret === secret &&
						event.data.source === 'INCLUDEAI_IFRAME_CHANNEL' &&
						event.data.response) {
						window.removeEventListener('message', allyListener);
						resolve(event.data.payload);
					}
				});
				// @ts-ignore
				iframe.contentWindow.postMessage({
					secret,
					command,
					args,
					options,
					source: 'INCLUDEAI_CONTENT_PAGE',
				}, '*');
			});
		} else if (to === 'allysidebar') {
			const iframe = document.getElementById(allySidebarID);
			const secret = 'secret_content_to_allysidebar_' + makeRandomString(16);
			return new Promise((resolve) => {
				window.addEventListener('message', function allySidebarListener(event) {
					if (event.data.secret === secret &&
						event.data.source === 'INCLUDEAI_SIDEBAR_CHANNEL' &&
						event.data.response) {
						window.removeEventListener('message', allySidebarListener);
						resolve(event.data.payload);
					}
				});
				// @ts-ignore
				iframe.contentWindow.postMessage({
					secret,
					command,
					args,
					options,
					source: 'INCLUDEAI_CONTENT_PAGE',
				}, '*');
			});
		} else { // content -> background route -> content (may not be the same one)
			return new Promise((resolve) => {
				// @ts-ignore
				chrome.runtime.sendMessage({
					command: 'routeToContent',
					payload: {
						command,
						args,
						options,
					},
				}, (response: any) => {
					resolve(response);
				});
			});
		}
	}
}

/*
Channel For ally page
*/
function respondToContentWithSecret(secret: string) {
	return (payload: any) => {
		parent.postMessage({
			source: 'INCLUDEAI_IFRAME_CHANNEL',
			secret,
			payload,
			response: true,
		}, '*');
	};
}

function respondToContentFromHQ(secret: string) {
	return (payload: any) => {
		parent.postMessage({
			source: 'INCLUDEAI_HQ_CHANNEL',
			secret,
			payload,
			response: true,
		}, '*');
	};
}

class AllyChannel extends Channel {
	constructor() {
		super();
		this.context = 'ally';

		emitter.addListener('sameContextInbound', this.listenToSameContext.bind(this));
		window.addEventListener('message', this.listenToContent.bind(this));
	}

	public listenToSameContext(msg: any) {
		if (!!msg.command) {
			const secret = msg.secret;
			const response = respondWithSecret(secret);
			if (Object.keys(this.handlers).includes(msg.command)) {
				emitter.emit(msg.command, { emitterSecret: msg.options.secret });
				this.handlers[msg.command](response, msg.options, msg.args);
			} else {
				response(`No command found for ${msg.command}`);
			}
		}
		return true;
	}

	public listenToContent(packet: any) {
		if ( packet.data.source === 'INCLUDEAI_CONTENT_PAGE') {
			const secret = packet.data.secret;
			const command = packet.data.command;
			const args = packet.data.args;
			const options = packet.data.options;

			let response = null;
			if (secret.startsWith('secret_content_to_hq_')) {
				response = respondToContentFromHQ(secret);
			} else {
				response = respondToContentWithSecret(secret);
			}
			if (Object.keys(this.handlers).includes(command)) {
				emitter.emit(command, { emitterSecret: options.secret });
				this.handlers[command](response, options, args);
			} else {
				response(`No command found for ${command}`);
			}
		}
	}

	public emit(event: any) {
		const command = event.command;
		const args = !!event.args ? event.args : [];
		const to = event.to;
		const options = !!event.options ? event.options : {};
		if (to === 'background') {
			return new Promise((resolve: any) => {
				// @ts-ignore
				chrome.runtime.sendMessage('ieobilbikjmiipkggaapehfhplcejbpb', {
					command,
					args,
					options,
				}, (response: any) => {
					resolve(response);
				});
			});
		} else if (to === 'content') {
			// @ts-ignore
			return new Promise((resolve: any) => {
				// @ts-ignore
				chrome.runtime.sendMessage('ieobilbikjmiipkggaapehfhplcejbpb', {
					command: 'routeToContent',
					payload: {
						command,
						args,
						options,
					},
				}, (response: any) => {
					resolve(response);
				});
			});
		} else if (to === 'parent') {
			const secret = 'secret_ally_to_content_' + makeRandomString(16);
			return new Promise((resolve) => {
				window.addEventListener('message', function contentListener(packet) {
					const msg = packet.data;
					if (msg.secret === secret && 
						msg.source === 'INCLUDEAI_CONTENT_PAGE' &&
						msg.response) {
						window.removeEventListener('message', contentListener);
						resolve(msg.payload);
					}
				});
				parent.postMessage({
					command,
					args,
					secret,
					options,
					source: 'INCLUDEAI_IFRAME_CHANNEL',
				}, '*');
			});

		}  else { // content -> content, background -> background, iframe -> iframe
			return super.emit(event);
		}
	}
}

/*
Channel For ally page
*/
function respondToContentFromSidebar(secret: string) {
	return (payload: any) => {
		parent.postMessage({
			source: 'INCLUDEAI_SIDEBAR_CHANNEL',
			secret,
			payload,
			response: true,
		}, '*');
	};
}
class AllySidebarChannel extends Channel {
	constructor() {
		super();
		this.context = 'allysidebar';

		emitter.addListener('sameContextInbound', this.listenToSameContext.bind(this));
		window.addEventListener('message', this.listenToContent.bind(this));
	}

	public listenToSameContext(msg: any) {
		if (!!msg.command) {
			const secret = msg.secret;
			const response = respondWithSecret(secret);
			if (Object.keys(this.handlers).includes(msg.command)) {
				emitter.emit(msg.command, { emitterSecret: msg.options.secret });
				this.handlers[msg.command](response, msg.options, msg.args);
			} else {
				response(`No command found for ${msg.command}`);
			}
		}
		return true;
	}

	public listenToContent(packet: any) {
		if ( packet.data.source === 'INCLUDEAI_CONTENT_PAGE') {
			const secret = packet.data.secret;
			const command = packet.data.command;
			const args = packet.data.args;
			const options = packet.data.options;
			const response = respondToContentFromSidebar(secret);
			if (Object.keys(this.handlers).includes(command)) {
				emitter.emit(command, { emitterSecret: options.secret });
				this.handlers[command](response, options, args);
			} else {
				response(`No command found for ${command}`);
			}
		}
	}

	public emit(event: any) {
		const command = event.command;
		const args = !!event.args ? event.args : [];
		const to = event.to;
		const options = !!event.options ? event.options : {};
		if (to === 'background') {
			return new Promise((resolve) => {
				// @ts-ignore
				chrome.runtime.sendMessage('ieobilbikjmiipkggaapehfhplcejbpb', {
					command,
					args,
					options,
				}, (response: any) => {
					resolve(response);
				});
			});
		} else if (to === 'content') {
			// @ts-ignore
			return new Promise((resolve: any) => {
				// @ts-ignore
				chrome.runtime.sendMessage('ieobilbikjmiipkggaapehfhplcejbpb', {
					command: 'routeToContent',
					payload: {
						command,
						args,
						options,
					},
				}, (response: any) => {
					resolve(response);
				});
			});
		} else if (to === 'parent') {
			const secret = 'secret_allysidebar_to_content_' + makeRandomString(16);
			return new Promise((resolve) => {
				window.addEventListener('message', function contentListener(packet) {
					const msg = packet.data;
					if (msg.secret === secret &&
						msg.source === 'INCLUDEAI_CONTENT_PAGE' &&
						msg.resposne) {
						window.removeEventListener('message', contentListener);
						resolve(msg.payload);
					}
				});
				parent.postMessage({
					command,
					args,
					secret,
					options,
					source: 'INCLUDEAI_SIDEBAR_CHANNEL',
				}, '*');
			});

		} else { // content -> content, background -> background, iframe -> iframe
			return super.emit(event);
		}
	}
}

/*
Channel For ally page
*/
// class HQChannel extends Channel {
// 	constructor() {
// 		super();
// 		this.context = 'hq';

// 		window.addEventListener('message', this.listenToContent.bind(this));
// 	}

// 	public listenToContent(packet: any) {
// 		if ( packet.data.source === 'INCLUDEAI_CONTENT_PAGE') {
// 			const secret = packet.data.secret;
// 			const command = packet.data.command;
// 			const args = packet.data.args;
// 			const options = packet.data.options;
// 			const response = respondToContentFromHQ(secret);
// 			if (Object.keys(this.handlers).includes(command)) {
// 				emitter.emit(command, { emitterSecret: options.secret });
// 				this.handlers[command](response, options, args);
// 			} else {
// 				response(`No command found for ${command}`);
// 			}
// 		}
// 	}

// 	public emit(event: any) {
// 		const command = event.command;
// 		const args = !!event.args ? event.args : [];
// 		const to = event.to;
// 		const options = !!event.options ? event.options : {};
// 		if (to === 'background') {
// 			return new Promise((resolve) => {
// 				// @ts-ignore
// 				chrome.runtime.sendMessage('ieobilbikjmiipkggaapehfhplcejbpb', {
// 					command,
// 					args,
// 					options,
// 				}, (response: any) => {
// 					resolve(response);
// 				});
// 			});
// 		} else if (to === 'parent') {
// 			const secret = 'secret_hq_to_content_' + makeRandomString(16);
// 			return new Promise((resolve) => {
// 				window.addEventListener('message', function contentListener(packet) {
// 					const msg = packet.data;
// 					if (msg.secret === secret &&
// 						msg.source === 'INCLUDEAI_CONTENT_PAGE' &&
// 						msg.resposne) {
// 						window.removeEventListener('message', contentListener);
// 						resolve(msg.payload);
// 					}
// 				});
// 				parent.postMessage({
// 					command,
// 					args,
// 					secret,
// 					options,
// 					source: 'INCLUDEAI_HQ_CHANNEL',
// 				}, '*');
// 			});
// 		} else {
// 			return super.emit(event);
// 		}
// 	}
// }
